import {
  BASE_SPEED,
  COLORS as C,
  GAME_CANVAS_HEIGHT,
  GAME_CANVAS_WIDTH,
  HIGH_SCORE_KEY,
  LANE_COUNT,
  LANE_W,
  ROAD_LEFT,
  ROAD_RIGHT,
  ROAD_W,
  START_LIVES
} from './constants';

export interface HudState {
  score: number;
  deals: number;
  lives: number;
  /** Speed fill ratio 0..1 for the speed bar. */
  speedPct: number;
}

export interface GameResult {
  score: number;
  deals: number;
  isNewRecord: boolean;
  best: number;
}

export interface EngineCallbacks {
  onHud: (hud: HudState) => void;
  onGameOver: (result: GameResult) => void;
}

type GameState = 'start' | 'playing' | 'gameover';

interface Player {
  lane: number;
  x: number;
  y: number;
  w: number;
  h: number;
  targetX: number;
  invincible: number;
}

interface Obstacle {
  lane: number;
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  speed: number;
}

interface Deal {
  lane: number;
  x: number;
  y: number;
  r: number;
  pulse: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  life: number;
  decay: number;
  color: string;
}

const laneX = (lane: number) => ROAD_LEFT + lane * LANE_W + LANE_W / 2;

const darken = (hex: string, amount: number) => {
  const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - amount);
  const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - amount);
  const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - amount);
  return `rgb(${r},${g},${b})`;
};

// Manual rounded-rect path (avoids relying on ctx.roundRect, which isn't in
// every supported browser or the TS 4.7 DOM lib). Builds the path; caller fills.
const roundRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number
) => {
  const r = Math.min(radius, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

/**
 * Framework-agnostic engine for the "Stora bilracet" canvas game. Ported from
 * the original single-file HTML/JS game. No DOM lookups by id, no leaderboard,
 * no name/email form — HUD/game-over are surfaced via callbacks so React owns
 * the surrounding chrome.
 */
export class CarRaceEngine {
  private ctx: CanvasRenderingContext2D;
  private cb: EngineCallbacks;

  private state: GameState = 'start';
  private score = 0;
  private deals = 0;
  private lives = START_LIVES;
  private frameCount = 0;
  private baseSpeed = BASE_SPEED;
  private gameSpeed = BASE_SPEED;
  private flashTimer = 0;
  private roadOffset = 0;
  private highScore = 0;

  private player: Player = {
    lane: 1,
    x: laneX(1),
    y: GAME_CANVAS_HEIGHT - 100,
    w: 38,
    h: 64,
    targetX: laneX(1),
    invincible: 0
  };

  private obstacles: Obstacle[] = [];
  private dealItems: Deal[] = [];
  private particles: Particle[] = [];

  private keys: Record<string, boolean> = {};
  private touchStartX = 0;
  private rafId = 0;
  private lastTime = 0;
  private accumulator = 0;
  private lastHud: HudState = { score: -1, deals: -1, lives: -1, speedPct: -1 };

  constructor(canvas: HTMLCanvasElement, callbacks: EngineCallbacks) {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('CarRaceEngine: 2D canvas context unavailable');
    }
    this.ctx = ctx;
    this.cb = callbacks;
    this.canvas = canvas;

    if (typeof window !== 'undefined') {
      this.highScore = parseInt(
        window.localStorage.getItem(HIGH_SCORE_KEY) || '0',
        10
      );
    }

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    canvas.addEventListener('touchstart', this.onTouchStart, { passive: true });
    canvas.addEventListener('touchend', this.onTouchEnd, { passive: true });

    // Initial static frame + HUD, then run the loop (idle until start()).
    this.drawRoad();
    this.drawCar(laneX(1), GAME_CANVAS_HEIGHT - 100, 38, 64, C.yellow, true);
    this.emitHud();
    this.rafId = requestAnimationFrame(this.loop);
  }

  private canvas: HTMLCanvasElement;

  // ---- Public API -------------------------------------------------------

  getBest() {
    return this.highScore;
  }

  start = () => {
    this.state = 'playing';
    this.score = 0;
    this.deals = 0;
    this.lives = START_LIVES;
    this.frameCount = 0;
    this.gameSpeed = this.baseSpeed;
    this.obstacles = [];
    this.dealItems = [];
    this.particles = [];
    this.player.lane = 1;
    this.player.x = laneX(1);
    this.player.targetX = laneX(1);
    this.player.invincible = 0;
    this.flashTimer = 0;
    this.emitHud();
  };

  moveLeft = () => this.handleMove('ArrowLeft');
  moveRight = () => this.handleMove('ArrowRight');

  destroy = () => {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.canvas.removeEventListener('touchstart', this.onTouchStart);
    this.canvas.removeEventListener('touchend', this.onTouchEnd);
  };

  // ---- Input ------------------------------------------------------------

  private onKeyDown = (e: KeyboardEvent) => {
    if (!this.keys[e.key]) {
      this.keys[e.key] = true;
      this.handleMove(e.key);
    }
  };

  private onKeyUp = (e: KeyboardEvent) => {
    this.keys[e.key] = false;
  };

  private onTouchStart = (e: TouchEvent) => {
    this.touchStartX = e.touches[0].clientX;
  };

  private onTouchEnd = (e: TouchEvent) => {
    const dx = e.changedTouches[0].clientX - this.touchStartX;
    if (Math.abs(dx) > 30) {
      this.handleMove(dx < 0 ? 'ArrowLeft' : 'ArrowRight');
    }
  };

  private handleMove(key: string) {
    if (this.state !== 'playing') {
      return;
    }
    if (key === 'ArrowLeft' || key === 'a' || key === 'A') {
      if (this.player.lane > 0) {
        this.player.lane--;
        this.player.targetX = laneX(this.player.lane);
      }
    } else if (key === 'ArrowRight' || key === 'd' || key === 'D') {
      if (this.player.lane < LANE_COUNT - 1) {
        this.player.lane++;
        this.player.targetX = laneX(this.player.lane);
      }
    }
  }

  // ---- Spawning ---------------------------------------------------------

  private spawnObstacle() {
    const lane = Math.floor(Math.random() * LANE_COUNT);
    // Avoid two obstacles in the same column too close together.
    const recent = this.obstacles.filter(o => o.lane === lane && o.y < -50);
    if (recent.length > 0) {
      return;
    }
    const carColors = [C.coral, '#c0392b', '#e74c3c', '#d35400', '#8e44ad'];
    const col = carColors[Math.floor(Math.random() * carColors.length)];
    this.obstacles.push({
      lane,
      x: laneX(lane),
      y: -50,
      w: 36,
      h: 50,
      color: col,
      speed: this.gameSpeed
    });
  }

  private spawnDeal() {
    const lane = Math.floor(Math.random() * LANE_COUNT);
    this.dealItems.push({ lane, x: laneX(lane), y: -30, r: 14, pulse: 0 });
  }

  private spawnParticle(x: number, y: number, color: string, count: number) {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1.5 + Math.random() * 3;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        r: 3 + Math.random() * 4,
        life: 1,
        decay: 0.03 + Math.random() * 0.04,
        color
      });
    }
  }

  // ---- Collision --------------------------------------------------------

  private checkCollision(
    a: { x: number; y: number; w: number; h: number },
    b: Obstacle
  ) {
    return (
      Math.abs(a.x - b.x) < a.w / 2 + b.w / 2 - 6 &&
      Math.abs(a.y - b.y) < a.h / 2 + b.h / 2 - 8
    );
  }

  private checkDealCollision(p: Player, d: Deal) {
    const dist = Math.hypot(p.x - d.x, p.y - d.y);
    return dist < p.w / 2 + d.r - 4;
  }

  // ---- Drawing ----------------------------------------------------------

  private drawRoad() {
    const { ctx } = this;
    ctx.fillStyle = C.road;
    ctx.fillRect(0, 0, GAME_CANVAS_WIDTH, GAME_CANVAS_HEIGHT);

    // Sidewalks
    ctx.fillStyle = '#1e2436';
    ctx.fillRect(0, 0, ROAD_LEFT, GAME_CANVAS_HEIGHT);
    ctx.fillRect(
      ROAD_RIGHT,
      0,
      GAME_CANVAS_WIDTH - ROAD_RIGHT,
      GAME_CANVAS_HEIGHT
    );

    // Road texture lines
    ctx.strokeStyle = 'rgba(255,255,255,0.03)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 8; i++) {
      const x = ROAD_LEFT + 10 + i * (ROAD_W / 8);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, GAME_CANVAS_HEIGHT);
      ctx.stroke();
    }

    // Dashed lane lines
    const dashLen = 40;
    const gapLen = 28;
    const totalLen = dashLen + gapLen;
    ctx.strokeStyle = '#3d4560';
    ctx.lineWidth = 2;
    ctx.setLineDash([dashLen, gapLen]);
    for (let l = 1; l < LANE_COUNT; l++) {
      const lx = ROAD_LEFT + l * LANE_W;
      const offset = this.roadOffset % totalLen;
      ctx.beginPath();
      ctx.moveTo(lx, -totalLen + offset);
      ctx.lineTo(lx, GAME_CANVAS_HEIGHT + totalLen);
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // Road edges
    ctx.strokeStyle = '#f5b40033';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(ROAD_LEFT, 0);
    ctx.lineTo(ROAD_LEFT, GAME_CANVAS_HEIGHT);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(ROAD_RIGHT, 0);
    ctx.lineTo(ROAD_RIGHT, GAME_CANVAS_HEIGHT);
    ctx.stroke();
  }

  private drawCar(
    x: number,
    y: number,
    w: number,
    h: number,
    color: string,
    isPlayer: boolean
  ) {
    const { ctx } = this;
    ctx.save();
    ctx.translate(x, y);

    if (
      isPlayer &&
      this.player.invincible > 0 &&
      Math.floor(this.player.invincible / 4) % 2 === 0
    ) {
      ctx.globalAlpha = 0.4;
    }

    const bx = -w / 2;
    const by = -h / 2;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.beginPath();
    ctx.ellipse(2, h / 2 - 4, w / 2 - 2, 8, 0, 0, Math.PI * 2);
    ctx.fill();

    // Main body
    ctx.fillStyle = color;
    roundRectPath(ctx, bx, by, w, h, 6);
    ctx.fill();

    // Roof
    const roofY = isPlayer ? by + h * 0.15 : by + h * 0.1;
    const roofH = h * 0.35;
    ctx.fillStyle = isPlayer ? '#1a2030' : darken(color, 30);
    roundRectPath(ctx, bx + w * 0.1, roofY, w * 0.8, roofH, 4);
    ctx.fill();

    // Windows
    ctx.fillStyle = isPlayer ? 'rgba(42,157,143,0.7)' : 'rgba(150,200,220,0.5)';
    roundRectPath(ctx, bx + w * 0.12, roofY + 4, w * 0.76, roofH - 8, 3);
    ctx.fill();

    if (isPlayer) {
      // Front headlights
      ctx.fillStyle = '#fff9c4';
      ctx.beginPath();
      ctx.ellipse(bx + w * 0.2, by + h * 0.06, 4, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(bx + w * 0.8, by + h * 0.06, 4, 3, 0, 0, Math.PI * 2);
      ctx.fill();

      // Player stripe
      ctx.fillStyle = C.yellow;
      ctx.fillRect(bx + 6, by + h * 0.55, w - 12, 3);

      // Kvdbil wordmark hint
      ctx.fillStyle = C.yellow;
      ctx.font = 'bold 7px Open Sans';
      ctx.textAlign = 'center';
      ctx.fillText('KVD', 0, by + h * 0.48);
    } else {
      // Taillights
      ctx.fillStyle = '#ff4444';
      ctx.beginPath();
      ctx.ellipse(bx + w * 0.2, by + h * 0.94, 3, 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(bx + w * 0.8, by + h * 0.94, 3, 2, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Wheels
    ctx.fillStyle = '#0d1020';
    const wy = [by + h * 0.18, by + h * 0.72];
    const wx = [bx - 2, bx + w - 4];
    for (const wyi of wy) {
      for (const wxi of wx) {
        roundRectPath(ctx, wxi, wyi, 6, 12, 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  private drawBarrier(x: number, y: number, w: number, h: number) {
    const { ctx } = this;
    ctx.save();
    ctx.translate(x, y);

    const cw = w * 0.55;
    const ch = h * 0.85;

    // Base
    ctx.fillStyle = '#e0e0e0';
    roundRectPath(ctx, -cw * 0.6, ch * 0.4, cw * 1.2, ch * 0.18, 2);
    ctx.fill();

    // Cone body — orange
    ctx.fillStyle = '#e85d04';
    ctx.beginPath();
    ctx.moveTo(0, -ch * 0.5);
    ctx.lineTo(cw * 0.5, ch * 0.42);
    ctx.lineTo(-cw * 0.5, ch * 0.42);
    ctx.closePath();
    ctx.fill();

    // White stripe
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.moveTo(-cw * 0.22, -ch * 0.05);
    ctx.lineTo(cw * 0.22, -ch * 0.05);
    ctx.lineTo(cw * 0.32, ch * 0.18);
    ctx.lineTo(-cw * 0.32, ch * 0.18);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  private drawDeal(d: Deal) {
    const { ctx } = this;
    const pulse = Math.sin(d.pulse) * 0.25;
    const r = d.r * (1 + pulse);

    ctx.save();
    ctx.translate(d.x, d.y);
    ctx.fillStyle = C.yellow;
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const outerAngle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
      const innerAngle = outerAngle + (2 * Math.PI) / 10;
      const ox = Math.cos(outerAngle) * r;
      const oy = Math.sin(outerAngle) * r;
      const ix = Math.cos(innerAngle) * r * 0.42;
      const iy = Math.sin(innerAngle) * r * 0.42;
      if (i === 0) {
        ctx.moveTo(ox, oy);
      } else {
        ctx.lineTo(ox, oy);
      }
      ctx.lineTo(ix, iy);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  private drawParticles() {
    const { ctx } = this;
    for (const p of this.particles) {
      ctx.save();
      ctx.globalAlpha = p.life;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  private drawFlash() {
    if (this.flashTimer > 0) {
      const { ctx } = this;
      ctx.fillStyle = `rgba(239,91,91,${(this.flashTimer / 20) * 0.4})`;
      ctx.fillRect(0, 0, GAME_CANVAS_WIDTH, GAME_CANVAS_HEIGHT);
    }
  }

  // ---- HUD --------------------------------------------------------------

  private emitHud() {
    const speedPct = Math.min(1, (this.gameSpeed - this.baseSpeed) / 8);
    const next: HudState = {
      score: this.score,
      deals: this.deals,
      lives: this.lives,
      speedPct
    };
    const prev = this.lastHud;
    if (
      next.score !== prev.score ||
      next.deals !== prev.deals ||
      next.lives !== prev.lives ||
      Math.abs(next.speedPct - prev.speedPct) > 0.01
    ) {
      this.lastHud = next;
      this.cb.onHud(next);
    }
  }

  // ---- Simulation -------------------------------------------------------

  private update() {
    if (this.flashTimer > 0) {
      this.flashTimer--;
    }

    if (this.state !== 'playing') {
      return;
    }

    this.frameCount++;
    this.roadOffset += this.gameSpeed;
    this.gameSpeed = this.baseSpeed + this.frameCount / 300;

    // Move player towards target lane
    const dx = this.player.targetX - this.player.x;
    this.player.x += dx * 0.18;

    if (this.player.invincible > 0) {
      this.player.invincible--;
    }

    // Spawn obstacles
    const spawnRate = Math.max(35, 80 - this.frameCount / 80);
    if (this.frameCount % Math.round(spawnRate) === 0) {
      this.spawnObstacle();
    }

    // Spawn deals
    const dealRate = Math.max(80, 160 - this.frameCount / 100);
    if (this.frameCount % Math.round(dealRate) === 0) {
      this.spawnDeal();
    }

    // Update obstacles
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const o = this.obstacles[i];
      o.y += o.speed + this.gameSpeed * 0.5;

      if (this.player.invincible === 0 && this.checkCollision(this.player, o)) {
        this.lives--;
        this.player.invincible = 90;
        this.flashTimer = 20;
        this.spawnParticle(this.player.x, this.player.y, C.coral, 16);
        this.obstacles.splice(i, 1);
        if (this.lives <= 0) {
          this.state = 'gameover';
          this.handleGameOver();
        }
        continue;
      }

      if (o.y > GAME_CANVAS_HEIGHT + 100) {
        this.obstacles.splice(i, 1);
      }
    }

    // Update deals
    for (let i = this.dealItems.length - 1; i >= 0; i--) {
      const d = this.dealItems[i];
      d.y += this.gameSpeed * 0.7;
      d.pulse += 0.12;

      if (this.checkDealCollision(this.player, d)) {
        this.deals++;
        this.score += 50;
        this.spawnParticle(d.x, d.y, C.yellow, 12);
        this.dealItems.splice(i, 1);
        continue;
      }

      if (d.y > GAME_CANVAS_HEIGHT + 50) {
        this.dealItems.splice(i, 1);
      }
    }

    // Score from driving
    if (this.frameCount % 10 === 0) {
      this.score++;
    }

    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.1;
      p.life -= p.decay;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
      }
    }

    this.emitHud();
  }

  private draw() {
    this.drawRoad();
    for (const d of this.dealItems) {
      this.drawDeal(d);
    }
    for (const o of this.obstacles) {
      this.drawBarrier(o.x, o.y, o.w, o.h);
    }
    this.drawCar(
      this.player.x,
      this.player.y,
      this.player.w,
      this.player.h,
      C.yellow,
      true
    );
    this.drawParticles();
    this.drawFlash();
  }

  // Simulation runs at a fixed 60Hz timestep regardless of display refresh
  // rate; rendering happens every animation frame. All tuning values in
  // update() are per-tick, so a 144Hz screen no longer speeds up the game.
  private static readonly STEP_MS = 1000 / 60;

  private loop = (now: number) => {
    if (this.lastTime === 0) {
      this.lastTime = now;
    }
    // Clamp long gaps (background tab, GC pause) so we don't burst-simulate.
    const elapsed = Math.min(now - this.lastTime, 250);
    this.lastTime = now;

    this.accumulator += elapsed;
    while (this.accumulator >= CarRaceEngine.STEP_MS) {
      this.update();
      this.accumulator -= CarRaceEngine.STEP_MS;
    }

    this.draw();
    this.rafId = requestAnimationFrame(this.loop);
  };

  private handleGameOver() {
    const isNewRecord = this.score > this.highScore;
    if (isNewRecord) {
      this.highScore = this.score;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(HIGH_SCORE_KEY, String(this.highScore));
      }
    }
    this.cb.onGameOver({
      score: this.score,
      deals: this.deals,
      isNewRecord,
      best: this.highScore
    });
  }
}
